import { stripFrontmatter, terms, termsMeta } from "@biteasy/legal";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import Markdown from "react-markdown";
import { authClient } from "@/auth";
import { Button } from "@/components/catalyst/Button";
import { Checkbox, CheckboxField } from "@/components/catalyst/Checkbox";
import { Label } from "@/components/catalyst/Fieldset";
import { Logo } from "@/components/Logo";
import { AuthLayout } from "../../components/catalyst/AuthLayout";
import { Heading, Subheading } from "../../components/catalyst/Heading";

const termsContent = stripFrontmatter(terms);

export function Terms() {
	const navigate = useNavigate();
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleAccept = async () => {
		if (!termsAccepted) return;

		setIsSubmitting(true);
		setError(null);

		try {
			await authClient.updateUser({
				acceptedTermsAt: new Date(),
			});
			navigate({ to: "/" });
		} catch (err) {
			setError(err instanceof Error ? err.message : "Something went wrong");
			setIsSubmitting(false);
		}
	};

	const handleDecline = () => {
		authClient.signOut({
			fetchOptions: {
				onSuccess() {
					navigate({ to: "/auth/signin" });
				},
			},
		});
	};

	return (
		<AuthLayout>
			<div className="flex flex-col gap-6 w-full max-w-2xl text-center">
				<div className="flex flex-col items-center gap-4">
					<Logo />
					<Heading>Terms of Service</Heading>
					<Subheading>
						Please review and accept the terms of service to continue.
					</Subheading>

					<div className="flex flex-col gap-6 w-full">
						<p className="text-sm text-base-content/60">
							Last updated:{" "}
							<time dateTime={termsMeta.pubDate.toISOString()}>
								{termsMeta.pubDate.toLocaleDateString("en-US", {
									year: "numeric",
									month: "long",
									day: "2-digit",
								})}
							</time>
						</p>

						{/* Scrollable terms content */}
						<div className="h-96 overflow-y-auto border border-base-300 rounded-lg p-6 text-left bg-base-200/50 prose prose-sm prose-invert max-w-none prose-headings:text-base-content prose-p:text-base-content/80 prose-strong:text-base-content prose-li:text-base-content/80 prose-li:marker:text-accent prose-a:text-accent prose-a:no-underline hover:prose-a:underline">
							<Markdown>{termsContent}</Markdown>
						</div>

						{error && <p className="text-error text-sm">{error}</p>}

						{/* Checkbox */}
						<CheckboxField>
							<Checkbox
								checked={termsAccepted}
								onChange={setTermsAccepted}
								color="indigo"
							/>
							<Label className="font-semibold">
								I have read and agree to the Terms of Service
							</Label>
						</CheckboxField>

						{/* Buttons */}
						<div className="flex gap-4">
							<Button
								color="dark/white"
								disabled={!termsAccepted || isSubmitting}
								onClick={handleAccept}
								className="flex-1"
							>
								{isSubmitting ? "Processing..." : "Accept & Continue"}
							</Button>
							<Button
								color="white"
								onClick={handleDecline}
								className="flex-1"
								disabled={isSubmitting}
							>
								Decline
							</Button>
						</div>
					</div>
				</div>
			</div>
		</AuthLayout>
	);
}
